import { morph } from '@theme/morph';

/**
 * Keeps the compatible-watches block in sync with the selected variant.
 *
 * `compatible_watches` is a VARIANT metafield, so each width (e.g. 18mm vs
 * 20mm) maps to its own set of watches. The block is server-rendered for the
 * current variant. On a simple variant change the variant-picker only morphs
 * itself (it does not re-render the rest of `<main>`), so this element re-reads
 * the freshly rendered section fragment carried by the `variant:update` event
 * and morphs its grid — which swaps both the watch cards and the
 * "Compatible wristband · Xmm" labels nested inside them.
 *
 * Because the section fetch takes a few hundred ms, a loading state is shown
 * optimistically on `variant:selected` (fired on click) and cleared on
 * `variant:update` (fired once the fragment resolves).
 *
 * A variant with no compatible watches is rendered with `hidden` (or omitted)
 * in the fresh fragment, so visibility is mirrored from there.
 *
 * @extends {HTMLElement}
 */
class CompatibleWatches extends HTMLElement {
  #selectors = {
    grid: '.compatible-watches__grid',
    section: '[id^="ProductInformation-"]',
  };

  #classes = {
    loading: 'compatible-watches--loading',
  };

  /** @type {Element | null} The product section this block belongs to. */
  #section = null;

  /**
   * Lifecycle - Component connected to DOM
   */
  connectedCallback() {
    this.#section = this.closest(this.#selectors.section);
    document.addEventListener('variant:selected', this.#onVariantSelected);
    document.addEventListener('variant:update', this.#onVariantUpdate);
  }

  /**
   * Lifecycle - Component removed from DOM
   */
  disconnectedCallback() {
    document.removeEventListener('variant:selected', this.#onVariantSelected);
    document.removeEventListener('variant:update', this.#onVariantUpdate);
  }

  /**
   * Shows the loading state as soon as the shopper picks an option, before the
   * section fetch resolves. Scoped to this product's section so that a variant
   * change on another product form does not dim this grid.
   * @param {Event} event - The variant:selected event.
   */
  #onVariantSelected = (event) => {
    const target = event.target;
    if (
      this.#section &&
      target instanceof Element &&
      target.closest(this.#selectors.section) !== this.#section
    ) {
      return;
    }

    this.#setLoading(true);
  };

  /**
   * Swaps the watch grid from the re-rendered variant fragment and clears the
   * loading state.
   * @param {Event} event - The variant:update event.
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {{ data?: { productId?: string, html?: Document } }} */ (
      /** @type {CustomEvent} */ (event).detail
    );

    // Only react to updates for this product (PDP can host several forms).
    const productId = this.dataset.productId;
    if (!productId || detail.data?.productId !== productId) return;

    this.#setLoading(false);

    const htmlDoc = detail.data?.html;
    if (!htmlDoc) return;

    const fresh = htmlDoc.querySelector(
      `compatible-watches[data-product-id="${productId}"]`
    );

    // No element or hidden element → this variant has no compatible watches.
    if (!fresh || fresh.hasAttribute('hidden')) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    const freshGrid = fresh.querySelector(this.#selectors.grid);
    const grid = this.querySelector(this.#selectors.grid);
    if (!freshGrid || !grid) return;

    // Morph keeps the <details> open/closed state untouched (we only swap the grid).
    morph(grid, freshGrid);
  };

  /**
   * Toggles the loading state (dimmed grid + spinner).
   * @param {boolean} state - Whether the block is loading.
   */
  #setLoading = (state) => {
    this.classList.toggle(this.#classes.loading, state);

    if (state) {
      this.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('aria-busy');
    }
  };
}

customElements.define('compatible-watches', CompatibleWatches);
